/* Koreva — comportements globaux (ES2020, sans dépendance) */
(() => {
  'use strict';

  const strings = (window.KorevaTheme && window.KorevaTheme.strings) || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Header : ombre au défilement + hauteur pour les panneaux ---------- */
  const setHeaderBottom = () => {
    const header = document.querySelector('[data-k-header]');
    if (!header) return;
    const bottom = Math.max(0, header.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty('--k-header-bottom', `${Math.round(bottom)}px`);
  };

  const onScroll = () => {
    const header = document.querySelector('[data-k-header]');
    if (header) header.classList.toggle('is-scrolled', window.scrollY > 8);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', setHeaderBottom, { passive: true });

  /* ---------- Menu mobile & recherche (details/summary) ---------- */
  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;

    if (details.matches('[data-k-menu-drawer]')) {
      setHeaderBottom();
      document.body.classList.toggle('k-scroll-lock', details.open);
    }
    if (details.matches('[data-k-search]') && details.open) {
      const input = details.querySelector('input[type="search"]');
      if (input) input.focus();
    }
    if (details.open && (details.matches('[data-k-menu-drawer]') || details.matches('[data-k-search]'))) {
      document.querySelectorAll('[data-k-menu-drawer][open], [data-k-search][open]').forEach((other) => {
        if (other !== details) other.open = false;
      });
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-k-menu-drawer][open], [data-k-search][open], .k-filters[open]').forEach((details) => {
      details.open = false;
      const summary = details.querySelector('summary');
      if (summary) summary.focus();
    });
  });

  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-k-search][open], .k-filters[open], [data-k-lang][open]').forEach((details) => {
      if (!details.contains(event.target)) details.open = false;
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const lang = document.querySelector('[data-k-lang][open]');
    if (!lang) return;
    lang.open = false;
    lang.querySelector('summary').focus();
  });

  /* ---------- Formulaires à soumission automatique (langue, filtres, tri) ---------- */
  let autosubmitTimer;
  document.addEventListener('change', (event) => {
    const form = event.target.closest('form[data-k-autosubmit]');
    if (!form) return;
    clearTimeout(autosubmitTimer);
    const delay = event.target.type === 'number' ? 600 : 0;
    autosubmitTimer = setTimeout(() => {
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    }, delay);
  });

  /* ---------- Sélecteurs de quantité (+ / −) ---------- */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-k-qty-step]');
    if (!button) return;
    const input = button.closest('.k-qty')?.querySelector('input');
    if (!input) return;
    const step = Number(input.step) || 1;
    const min = input.min === '' ? 0 : Number(input.min);
    const max = input.max === '' ? Infinity : Number(input.max);
    const direction = Number(button.dataset.kQtyStep);
    const next = Math.min(max, Math.max(min, (Number(input.value) || 0) + direction * step));
    if (next === Number(input.value)) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Footer : accordéons fermés sur mobile ---------- */
  const initFooterAccordions = () => {
    if (window.matchMedia('(min-width: 750px)').matches) return;
    document.querySelectorAll('[data-k-footer-accordion]').forEach((details) => { details.open = false; });
  };

  /* ---------- Apparition au défilement ---------- */
  let revealObserver;
  const initReveal = (root = document) => {
    const items = root.querySelectorAll('[data-k-reveal]:not(.is-visible)');
    if (!document.body.classList.contains('k-animate') || reducedMotion || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    // Déjà visibles au chargement : affichés immédiatement, sans animation.
    items.forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('is-visible');
    });
    document.documentElement.classList.add('k-reveal-ready');
    revealObserver = revealObserver || new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    items.forEach((el) => { if (!el.classList.contains('is-visible')) revealObserver.observe(el); });
  };

  /* ---------- Rotation de la barre d'annonce ---------- */
  class KRotator extends HTMLElement {
    connectedCallback() {
      this.items = [...this.children];
      if (this.items.length < 2 || reducedMotion) return;
      this.index = 0;
      const interval = Number(this.dataset.interval) || 5000;
      this.timer = setInterval(() => this.next(), interval);
      this.addEventListener('mouseenter', () => { this.paused = true; });
      this.addEventListener('mouseleave', () => { this.paused = false; });
    }
    disconnectedCallback() { clearInterval(this.timer); }
    next() {
      if (this.paused) return;
      this.items[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].classList.add('is-active');
    }
  }
  if (!customElements.get('k-rotator')) customElements.define('k-rotator', KRotator);

  /* ---------- Avant / Après : paires de photos en fondu ---------- */
  class KBeforeAfter extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-k-ba-slide]')];
      this.dots = [...this.querySelectorAll('[data-k-ba-dot]')];
      if (this.slides.length < 2) return;
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => { this.show(i); this.restart(); }));
      ['mouseenter', 'focusin'].forEach((e) => this.addEventListener(e, () => { this.paused = true; }));
      ['mouseleave', 'focusout'].forEach((e) => this.addEventListener(e, () => { this.paused = false; }));
      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          const i = this.slides.indexOf(e.target);
          if (i > -1) { this.show(i); this.paused = true; }
        });
        document.addEventListener('shopify:block:deselect', () => { this.paused = false; });
      }
      this.restart();
    }
    disconnectedCallback() { clearInterval(this.timer); }
    restart() {
      clearInterval(this.timer);
      if (reducedMotion) return;
      this.timer = setInterval(() => {
        if (!this.paused && !document.hidden) this.show((this.index + 1) % this.slides.length);
      }, Number(this.dataset.interval) || 3000);
    }
    show(i) {
      this.slides.forEach((slide, n) => {
        slide.classList.toggle('is-active', n === i);
        slide.toggleAttribute('aria-hidden', n !== i);
      });
      this.dots.forEach((dot, n) => {
        dot.classList.toggle('is-active', n === i);
        if (n === i) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current');
      });
      this.index = i;
    }
  }
  if (!customElements.get('k-before-after')) customElements.define('k-before-after', KBeforeAfter);

  /* ---------- Vidéos influenceuses ---------- */
  const videoObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting && !reducedMotion) {
          if (video.preload === 'none') video.preload = 'metadata';
          const playing = video.play();
          if (playing && playing.catch) playing.catch(() => {});
        } else {
          video.pause();
        }
      });
    }, { threshold: 0.6 })
    : null;

  class KVideoCard extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('[data-k-video-sound]');
      if (!this.video) return;
      this.video.muted = true;
      this.video.addEventListener('play', () => this.classList.add('is-playing'));
      this.video.addEventListener('pause', () => this.classList.remove('is-playing'));
      if (videoObserver) videoObserver.observe(this.video);
      if (this.button) this.button.addEventListener('click', () => this.toggleSound());
      this.video.addEventListener('click', () => this.toggleSound());
    }
    disconnectedCallback() {
      if (videoObserver && this.video) videoObserver.unobserve(this.video);
    }
    toggleSound() {
      const unmute = this.video.muted;
      if (unmute) {
        document.querySelectorAll('k-video-card').forEach((card) => {
          if (card !== this && typeof card.mute === 'function') card.mute();
        });
      }
      this.video.muted = !unmute;
      if (unmute) {
        const playing = this.video.play();
        if (playing && playing.catch) playing.catch(() => {});
      }
      this.updateButton();
    }
    mute() {
      if (!this.video) return;
      this.video.muted = true;
      this.updateButton();
    }
    updateButton() {
      if (!this.button) return;
      const soundOn = !this.video.muted;
      this.button.setAttribute('aria-pressed', String(soundOn));
      const label = this.button.querySelector('[data-k-video-sound-label]');
      if (label) label.textContent = soundOn ? strings.soundOff : strings.soundOn;
    }
  }
  if (!customElements.get('k-video-card')) customElements.define('k-video-card', KVideoCard);

  /* ---------- Étapes de livraison : dates estimées (jours ouvrés) ---------- */
  const addBusinessDays = (date, days) => {
    const result = new Date(date);
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      const day = result.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    return result;
  };

  class KDeliverySteps extends HTMLElement {
    connectedCallback() {
      const locale = document.documentElement.lang || 'fr';
      let format;
      try {
        format = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
      } catch (e) {
        return;
      }
      const today = new Date();
      this.querySelectorAll('time[data-k-date-min]').forEach((el) => {
        const min = Number(el.dataset.kDateMin);
        const max = Number(el.dataset.kDateMax);
        if (min === 0 && max === 0) return;
        const from = addBusinessDays(today, min);
        const to = addBusinessDays(today, max);
        el.dateTime = from.toISOString().slice(0, 10);
        el.textContent = min === max ? format.format(from) : `${format.format(from)} – ${format.format(to)}`;
      });
    }
  }
  if (!customElements.get('k-delivery-steps')) customElements.define('k-delivery-steps', KDeliverySteps);

  /* ---------- Recommandations produit (chargées à l'approche) ---------- */
  class KRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.loaded || !this.dataset.url) return;
      const load = () => {
        this.loaded = true;
        fetch(this.dataset.url)
          .then((response) => response.text())
          .then((html) => {
            const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('k-recommendations');
            if (fresh && fresh.innerHTML.trim()) {
              this.innerHTML = fresh.innerHTML;
              initReveal(this);
            }
          })
          .catch(() => {});
      };
      if (!('IntersectionObserver' in window)) return load();
      const observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        load();
      }, { rootMargin: '400px 0px' });
      observer.observe(this);
    }
  }
  if (!customElements.get('k-recommendations')) customElements.define('k-recommendations', KRecommendations);

  /* ---------- Bande défilante : bouton pause (WCAG 2.2.2) ---------- */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-k-marquee-toggle]');
    if (!toggle) return;
    const marquee = toggle.closest('[data-k-marquee]');
    const paused = marquee.classList.toggle('is-paused');
    toggle.setAttribute('aria-pressed', String(paused));
    const label = paused ? strings.marqueePlay : strings.marqueePause;
    if (toggle.hasAttribute('aria-label')) toggle.setAttribute('aria-label', label);
    else toggle.textContent = label;
  });

  /* ---------- Onglets accessibles (FAQ, formules) ---------- */
  class KTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll(':scope > .k-tabs__list [role="tab"]')];
      if (!this.tabs.length) return;
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => {
          const keys = { ArrowRight: 1, ArrowLeft: -1, Home: -Infinity, End: Infinity };
          if (!(event.key in keys)) return;
          event.preventDefault();
          let next = index + keys[event.key];
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          next = (next + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        });
      });
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
    }
  }
  if (!customElements.get('k-tabs')) customElements.define('k-tabs', KTabs);

  /* ---------- Carrousel horizontal (scroll-snap + flèches) ---------- */
  class KCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-k-carousel-track]');
      if (!this.track) return;
      const step = (direction) => {
        const card = this.track.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const width = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth;
        this.track.scrollBy({ left: direction * width, behavior: reducedMotion ? 'auto' : 'smooth' });
      };
      const prev = this.querySelector('[data-k-carousel-prev]');
      const next = this.querySelector('[data-k-carousel-next]');
      if (prev) prev.addEventListener('click', () => step(-1));
      if (next) next.addEventListener('click', () => step(1));
      const update = () => {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        if (prev) prev.disabled = this.track.scrollLeft <= 2;
        if (next) next.disabled = this.track.scrollLeft >= max;
        this.classList.toggle('is-static', max <= 0);
      };
      this.track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
    }
  }
  if (!customElements.get('k-carousel')) customElements.define('k-carousel', KCarousel);

  /* ---------- Initialisation + éditeur de thème ---------- */
  const init = () => {
    setHeaderBottom();
    onScroll();
    initFooterAccordions();
    initReveal();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', (event) => {
    initReveal(event.target);
    setHeaderBottom();
  });
})();
